import React, { useState, useEffect } from 'react';
import { FaSpinner, FaMoneyBill, FaSearch, FaFilter, FaTimes, FaCalendar, FaDownload, FaEye, FaCheck, FaTimes as FaTimesIcon, FaImage } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';

const AdminPayments = () => {
    const [loading, setLoading] = useState(true);
    const [payments, setPayments] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
    });
    const [showFilters, setShowFilters] = useState(false);
    const [tempSearch, setTempSearch] = useState('');
    const [filters, setFilters] = useState({
        payment_method: '',
        payment_status: '',
        start_date: '',
        end_date: '',
        search: ''
    });
    const [summary, setSummary] = useState({
        total_amount: 0,
        completed_amount: 0,
        pending_amount: 0,
        failed_amount: 0
    });
    
    // Modal states
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [verifying, setVerifying] = useState(false);
    const [adminNotes, setAdminNotes] = useState('');

    useEffect(() => {
        fetchPayments();
    }, [pagination.page, filters]);

    const fetchPayments = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const queryParams = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            });
            
            const response = await axios.get(`https://app.bihebari.com.np/api/admin/payments?${queryParams}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setPayments(response.data.data || []);
                setPagination(response.data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 });
                if (response.data.summary) {
                    setSummary(response.data.summary);
                }
            }
        } catch (error) {
            console.error('Error fetching payments:', error);
            toast.error('Error loading payments');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters({ ...filters, [name]: value });
        setPagination({ ...pagination, page: 1 });
    };

    const handleSearchClick = () => {
        setFilters({ ...filters, search: tempSearch });
        setPagination({ ...pagination, page: 1 });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearchClick();
        }
    };

    const handleResetFilters = () => {
        setFilters({
            payment_method: '',
            payment_status: '',
            start_date: '',
            end_date: '',
            search: ''
        });
        setTempSearch('');
        setPagination({ ...pagination, page: 1 });
    };

    const exportToCSV = () => {
        if (!payments || payments.length === 0) {
            toast.warning('No data to export');
            return;
        }
        
        const headers = ['ID', 'User Name', 'Email', 'Contact', 'Payment Method', 'Amount', 'Status', 'Date'];
        const csvData = payments.map(payment => [
            payment.id || '',
            payment.name || '',
            payment.email || '-',
            payment.contact || '-',
            getPaymentMethodLabel(payment.pay_method),
            payment.amount || 0,
            getPaymentStatusLabel(payment.payment_status),
            payment.created ? new Date(payment.created).toLocaleString() : '-'
        ]);
        
        const csvContent = [headers, ...csvData].map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payments_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export started');
    };

    const getPaymentMethodLabel = (method) => {
        const methods = {
            'esewa': 'Esewa',
            'khalti': 'Khalti',
            'connectips': 'ConnectIPS',
            'bank': 'Bank Transfer'
        };
        return methods[method] || method || 'Unknown';
    };

    const getPaymentStatusLabel = (status) => {
        const statuses = {
            'success': 'Completed',
            'completed': 'Completed',
            'pending': 'Pending',
            'failed': 'Failed'
        };
        return statuses[status] || status || 'Unknown';
    };

    const getPaymentStatusColor = (status) => {
        switch(status) {
            case 'success':
            case 'completed':
                return 'bg-green-100 text-green-800';
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'failed':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatCurrency = (amount) => {
        if (!amount && amount !== 0) return '₹0';
        return `Rs${parseInt(amount).toLocaleString()}`;
    };

    const viewPaymentDetails = (payment) => {
        setSelectedPayment(payment);
        setAdminNotes('');
        setShowPaymentModal(true);
    };

    const verifyBankPayment = async (paymentId, status) => {
        const statusText = status === 'success' ? 'approve' : 'reject';
        if (!window.confirm(`Are you sure you want to ${statusText} this payment?`)) return;
        
        setVerifying(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.put(
                `https://app.bihebari.com.np/api/payment/bank/verify/${paymentId}`,
                { 
                    status: status, 
                    admin_notes: adminNotes || `Payment ${statusText}d by admin on ${new Date().toLocaleString()}`
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            if (response.data.success) {
                toast.success(`Payment ${statusText}d successfully`);
                fetchPayments();
                setShowPaymentModal(false);
                setSelectedPayment(null);
            } else {
                toast.error(response.data.message || 'Error updating payment status');
            }
        } catch (error) {
            console.error('Verification error:', error);
            toast.error(error.response?.data?.message || 'Error updating payment status');
        } finally {
            setVerifying(false);
        }
    };

    const parsePaymentData = (paymentData) => {
        try {
            if (typeof paymentData === 'string') {
                return JSON.parse(paymentData);
            }
            return paymentData || {};
        } catch (e) {
            return {};
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <FaSpinner className="text-4xl text-indigo-600 animate-spin" />
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Payment Transactions</h1>
                <button onClick={exportToCSV} className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition">
                    <FaDownload />
                    <span>Export CSV</span>
                </button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-4 text-white">
                    <p className="text-sm opacity-90">Total Amount</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary?.total_amount)}</p>
                </div>
                <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-4 text-white">
                    <p className="text-sm opacity-90">Completed</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary?.completed_amount)}</p>
                </div>
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl shadow-lg p-4 text-white">
                    <p className="text-sm opacity-90">Pending</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary?.pending_amount)}</p>
                </div>
                <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-xl shadow-lg p-4 text-white">
                    <p className="text-sm opacity-90">Failed</p>
                    <p className="text-2xl font-bold">{formatCurrency(summary?.failed_amount)}</p>
                </div>
            </div>

            {/* Search and Filter Bar */}
            <div className="bg-white rounded-xl shadow-md p-4 mb-6">
                <div className="flex gap-4 mb-4">
                    <div className="flex-1">
                        <input
                            type="text"
                            placeholder="Search by user name, email or mobile..."
                            value={tempSearch}
                            onChange={(e) => setTempSearch(e.target.value)}
                            onKeyPress={handleKeyPress}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                        />
                    </div>
                    <button 
                        onClick={handleSearchClick} 
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded-lg flex items-center space-x-2 transition"
                    >
                        <FaSearch />
                        <span>Search</span>
                    </button>
                    <button onClick={() => setShowFilters(!showFilters)} className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition">
                        <FaFilter />
                        <span>Filters</span>
                    </button>
                </div>

                {filters.search && (
                    <div className="mb-3">
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                            <FaSearch className="text-xs" />
                            Searching for: "{filters.search}"
                            <button onClick={() => handleResetFilters()} className="hover:text-blue-900">
                                <FaTimes size={12} />
                            </button>
                        </span>
                    </div>
                )}

                {showFilters && (
                    <div className="border-t pt-4 mt-2">
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                                <select name="payment_method" value={filters.payment_method} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                                    <option value="">All Methods</option>
                                    <option value="esewa">Esewa</option>
                                    <option value="khalti">Khalti</option>
                                    <option value="connectips">ConnectIPS</option>
                                    <option value="bank">Bank Transfer</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Payment Status</label>
                                <select name="payment_status" value={filters.payment_status} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500">
                                    <option value="">All Status</option>
                                    <option value="success">Completed</option>
                                    <option value="pending">Pending</option>
                                    <option value="failed">Failed</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                                <input type="date" name="start_date" value={filters.start_date} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">End Date</label>
                                <input type="date" name="end_date" value={filters.end_date} onChange={handleFilterChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500" />
                            </div>
                        </div>
                        <div className="flex justify-end mt-4">
                            <button onClick={handleResetFilters} className="flex items-center space-x-2 text-red-600 hover:text-red-800">
                                <FaTimes />
                                <span>Reset Filters</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Payments Table */}
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payment Method</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {payments && payments.length > 0 ? (
                                payments.map((payment) => (
                                    <tr key={payment.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-900">{payment.id}</td>
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{payment.name || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{payment.email || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600 capitalize">
                                            {getPaymentMethodLabel(payment.pay_method)}
                                        </td>
                                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                                            {formatCurrency(payment.amount)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs rounded-full ${getPaymentStatusColor(payment.payment_status)}`}>
                                                {getPaymentStatusLabel(payment.payment_status)}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {payment.created ? new Date(payment.created).toLocaleString() : '-'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => viewPaymentDetails(payment)}
                                                className="text-indigo-600 hover:text-indigo-800 transition p-1"
                                                title="View Details"
                                            >
                                                <FaEye size={18} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                                        No payment records found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {pagination && pagination.totalPages > 1 && (
                    <div className="flex justify-center space-x-2 py-4 border-t">
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                            disabled={pagination.page === 1}
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            Previous
                        </button>
                        <span className="px-3 py-1 text-gray-600">
                            Page {pagination.page} of {pagination.totalPages}
                        </span>
                        <button
                            onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                            disabled={pagination.page === pagination.totalPages}
                            className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50"
                        >
                            Next
                        </button>
                    </div>
                )}
            </div>

            {/* Payment Details Modal */}
            {showPaymentModal && selectedPayment && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
                            <h3 className="text-xl font-bold">Payment Details</h3>
                            <button onClick={() => setShowPaymentModal(false)} className="text-gray-500 hover:text-gray-700">
                                <FaTimesIcon size={20} />
                            </button>
                        </div>
                        
                        <div className="p-6 space-y-4">
                            {/* Basic Info */}
                            <div className="grid grid-cols-2 gap-3">
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Payment ID</p>
                                    <p className="font-semibold">#{selectedPayment.id}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">User ID</p>
                                    <p className="font-semibold">#{selectedPayment.user_id}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">User Name</p>
                                    <p className="font-semibold">{selectedPayment.name || '-'}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Contact</p>
                                    <p className="font-semibold">{selectedPayment.contact || '-'}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Amount</p>
                                    <p className="font-semibold text-green-600">{formatCurrency(selectedPayment.amount)}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Payment Method</p>
                                    <p className="font-semibold">{getPaymentMethodLabel(selectedPayment.pay_method)}</p>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Status</p>
                                    <span className={`inline-block px-2 py-1 text-xs rounded-full ${getPaymentStatusColor(selectedPayment.payment_status)}`}>
                                        {getPaymentStatusLabel(selectedPayment.payment_status)}
                                    </span>
                                </div>
                                <div className="bg-gray-50 p-3 rounded">
                                    <p className="text-xs text-gray-500">Date</p>
                                    <p className="font-semibold">{selectedPayment.created ? new Date(selectedPayment.created).toLocaleString() : '-'}</p>
                                </div>
                            </div>

                            {/* Bank Transfer Details */}
                            {selectedPayment.pay_method === 'bank' && selectedPayment.payment_data && (
                                <div className="border-t pt-4">
                                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                                        <FaMoneyBill className="text-gray-500" />
                                        Bank Transfer Details
                                    </h4>
                                    {(() => {
                                        const paymentData = parsePaymentData(selectedPayment.payment_data);
                                        return (
                                            <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                                                <div className="grid grid-cols-2 gap-3">
                                                    <div>
                                                        <p className="text-xs text-gray-500">Transaction ID</p>
                                                        <p className="font-mono text-sm font-semibold">{paymentData.transaction_id || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Bank Name</p>
                                                        <p className="font-semibold">{paymentData.bank_name || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Account Name</p>
                                                        <p className="font-semibold">{paymentData.account_name || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Account Number</p>
                                                        <p className="font-mono font-semibold">{paymentData.account_number || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Payment Date</p>
                                                        <p className="font-semibold">{paymentData.payment_date || 'N/A'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="text-xs text-gray-500">Submitted At</p>
                                                        <p className="font-semibold">{paymentData.submitted_at ? new Date(paymentData.submitted_at).toLocaleString() : 'N/A'}</p>
                                                    </div>
                                                </div>
                                                
                                                {paymentData.notes && (
                                                    <div>
                                                        <p className="text-xs text-gray-500">User Notes</p>
                                                        <p className="text-sm bg-white p-2 rounded border">{paymentData.notes}</p>
                                                    </div>
                                                )}
                                                
                                                {paymentData.admin_notes && (
                                                    <div>
                                                        <p className="text-xs text-gray-500">Admin Notes</p>
                                                        <p className="text-sm bg-blue-50 p-2 rounded border border-blue-200 text-blue-800">{paymentData.admin_notes}</p>
                                                    </div>
                                                )}
                                                
                                                {paymentData.proof_image && (
                                                    <div>
                                                        <p className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                                                            <FaImage />
                                                            Payment Proof
                                                        </p>
                                                        <img 
                                                            src={`https://app.bihebari.com.np${paymentData.proof_image}`} 
                                                            alt="Payment proof"
                                                            className="max-w-full h-auto max-h-64 rounded border shadow-sm cursor-pointer"
                                                            onClick={() => window.open(`https://app.bihebari.com.np${paymentData.proof_image}`, '_blank')}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })()}
                                    
                                    {/* Verification Actions for Pending Bank Transfers */}
                                    {selectedPayment.payment_status === 'pending' && (
                                        <div className="mt-4 space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes (Optional)</label>
                                                <textarea
                                                    value={adminNotes}
                                                    onChange={(e) => setAdminNotes(e.target.value)}
                                                    rows="2"
                                                    placeholder="Add any notes about this verification..."
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                                                />
                                            </div>
                                            <div className="flex gap-3">
                                                <button
                                                    onClick={() => verifyBankPayment(selectedPayment.id, 'success')}
                                                    disabled={verifying}
                                                    className="flex-1 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                                                >
                                                    <FaCheck />
                                                    <span>Approve Payment</span>
                                                </button>
                                                <button
                                                    onClick={() => verifyBankPayment(selectedPayment.id, 'failed')}
                                                    disabled={verifying}
                                                    className="flex-1 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
                                                >
                                                    <FaTimesIcon />
                                                    <span>Reject Payment</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Other Payment Methods Details */}
                            {selectedPayment.pay_method !== 'bank' && selectedPayment.payment_data && (
                                <div className="border-t pt-4">
                                    <h4 className="font-semibold mb-2">Payment Response</h4>
                                    <pre className="bg-gray-50 p-3 rounded text-xs overflow-x-auto">
                                        {JSON.stringify(parsePaymentData(selectedPayment.payment_data), null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminPayments;